import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_MY_USER_MEDIA, STOCK_MEDIA_SEARCH, TRACK_STOCK_MEDIA_USE } from '@/queries/queries';
import { SOUND_EFFECTS, type SoundEffectsQueryData, type SoundEffectsQueryVars } from '@/queries/soundEffects';
import type { StockMediaItem } from '@/lib/stock-media/types';
import type { MyUserMediaRow } from '@/lib/stock-media/userMediaToStockItem';
import { probeImageSize } from '@/lib/layers/probeImageSize';
import { probeMediaMetadata } from '@/lib/layers/probeMediaMetadata';
import {
  soundEffectToAssistantMedia,
  stockItemToAssistantMedia,
  userMediaRowToAssistantMedia,
} from '@/lib/ai-assistant/editor/mediaTools';
import type { AssistantMediaItem } from '@/lib/ai-assistant/editor/mediaTypes';

// editor_search_media for the AI assistant: Pexels (and Unsplash for images)
// through the backend's stockMediaSearch, the user's My Media, or the
// sound-effect library (soundEffects). My Media rows saved without
// dimensions are probed so placements keep the aspect.

export interface AssistantMediaQuery {
  kind: 'image' | 'video' | 'audio';
  query: string;
  source: 'stock' | 'my_media' | 'sfx';
  /** Sound-effect category (source sfx only). */
  category?: string;
}

const MAX_RESULTS = 8;
const PROBE_TIMEOUT_MS = 5000;

const withTimeout = <T,>(promise: Promise<T>, fallback: T) => Promise.race([
  promise,
  new Promise<T>((resolve) => { setTimeout(() => resolve(fallback), PROBE_TIMEOUT_MS); }),
]);

async function withDimensions(item: AssistantMediaItem): Promise<AssistantMediaItem> {
  if (item.kind === 'image' && (!item.width || !item.height)) {
    const size = await withTimeout(probeImageSize(item.url), { width: 0, height: 0 });
    return size.width > 0 ? { ...item, width: size.width, height: size.height } : item;
  }
  if (item.kind !== 'image' && (!item.duration || (item.kind === 'video' && !item.width))) {
    const meta = await withTimeout(probeMediaMetadata(item.url, item.kind), { duration: 0, width: 0, height: 0 });
    return {
      ...item,
      duration: item.duration || meta.duration || null,
      width: item.width || meta.width || null,
      height: item.height || meta.height || null,
    };
  }
  return item;
}

/** Alternate the providers' results so both show in the first 8. */
function interleave<T>(lists: T[][]): T[] {
  const out: T[] = [];
  for (let index = 0; out.length < MAX_RESULTS && lists.some((list) => index < list.length); index += 1) {
    lists.forEach((list) => {
      if (index < list.length && out.length < MAX_RESULTS) out.push(list[index]);
    });
  }
  return out;
}

export function useAssistantMediaSearch() {
  const client = useApolloClient();

  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'pt';

  const searchMedia = useCallback(async ({ kind, query, source, category }: AssistantMediaQuery): Promise<AssistantMediaItem[]> => {
    if (source === 'sfx') {
      const { data } = await client.query<SoundEffectsQueryData, SoundEffectsQueryVars>({
        query: SOUND_EFFECTS,
        variables: { query: query || null, category: category ?? null, locale, limit: MAX_RESULTS },
        fetchPolicy: 'no-cache',
      });
      return (data?.soundEffects ?? []).map(soundEffectToAssistantMedia);
    }
    if (source === 'my_media') {
      const { data } = await client.query({
        query: GET_MY_USER_MEDIA,
        variables: { kind, search: query || undefined, limit: MAX_RESULTS, offset: 0 },
        fetchPolicy: 'no-cache',
      });
      const rows: MyUserMediaRow[] = data?.myUserMedia ?? [];
      const items = rows.map(userMediaRowToAssistantMedia).filter((item): item is AssistantMediaItem => !!item);
      return Promise.all(items.slice(0, MAX_RESULTS).map(withDimensions));
    }
    if (kind === 'audio') throw new Error('stock_has_no_audio');
    const providers = kind === 'video' ? ['pexels'] : ['pexels', 'unsplash'];
    const lists = await Promise.all(providers.map(async (provider) => {
      try {
        const { data } = await client.query({
          query: STOCK_MEDIA_SEARCH,
          variables: { kind, query: query || null, page: 1, provider },
          fetchPolicy: 'no-cache',
        });
        const items: StockMediaItem[] = data?.stockMediaSearch?.items ?? [];
        return items.map(stockItemToAssistantMedia);
      } catch {
        return [];
      }
    }));
    return interleave(lists);
  }, [client, locale]);

  /** Unsplash asks for a download event when a photo is used. */
  const trackStockUse = useCallback((item: AssistantMediaItem) => {
    if (item.provider !== 'unsplash' || !item.downloadLocation) return;
    client.mutate({
      mutation: TRACK_STOCK_MEDIA_USE,
      variables: { provider: 'unsplash', download_location: item.downloadLocation },
    }).catch(() => {});
  }, [client]);

  return { searchMedia, trackStockUse };
}
